// Build405: Italian editor localization; historical predecessor retained.
/* Build360: retain portable artwork across same-tab customer sign-in. */
(() => {
  'use strict';
  if (window.SmoofLoginDraft) return;
  const PARAM = 'smoof_resume';
  const MAX_AGE = 2 * 60 * 60 * 1000;
  const text = (de, en) => window.SmoofI18n.copy(de, en);
  let busy = false;
  let didRestore = false;
  function database() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('smoof-login-drafts-v1', 1);
      let expired = false;
      const timer = setTimeout(() => { expired = true; reject(new Error('Draft storage timeout')); }, 8000);
      request.onupgradeneeded = () => request.result.createObjectStore('drafts', { keyPath: 'id' });
      request.onerror = () => { clearTimeout(timer); reject(request.error); };
      request.onblocked = () => { expired = true; clearTimeout(timer); reject(new Error('Draft storage blocked')); };
      request.onsuccess = () => { clearTimeout(timer); if (expired) request.result.close(); else resolve(request.result); };
    });
  }
  async function stored(mode, action) {
    const db = await database();
    try {
      return await new Promise((resolve, reject) => {
        const tx = db.transaction('drafts', mode);
        const request = action(tx.objectStore('drafts'));
        tx.oncomplete = () => resolve(request?.result);
        tx.onerror = tx.onabort = () => reject(tx.error || new Error('Draft storage failed'));
      });
    } finally { db.close(); }
  }
  function cover(message, previous = document.activeElement) {
    const returnToLogin = previous?.matches?.('[data-smoof-design-login]');
    const element = document.createElement('dialog');
    element.dataset.smoofLoginDraftStatus = 'true';
    element.setAttribute('aria-labelledby', 'smoof-login-title');
    element.setAttribute('aria-describedby', 'smoof-login-message');
    const title = document.createElement('h2'); title.id = 'smoof-login-title';
    title.textContent = text('Ihr Design bleibt bei Ihnen', 'Keep your design with you');
    const copy = document.createElement('p'); copy.id = 'smoof-login-message';
    copy.setAttribute('role', 'status'); copy.textContent = message;
    const spinner = document.createElement('span'); spinner.dataset.smoofLoginSpinner = 'true'; spinner.setAttribute('aria-hidden', 'true');
    element.append(spinner, title, copy);
    let locked = true;
    const view = { element, copy,
      lock() { locked = true; element.setAttribute('aria-busy', 'true'); },
      release() { locked = false; spinner.remove(); element.removeAttribute('aria-busy'); },
      close() {
        window.removeEventListener('keydown', protectKeys, true);
        element.close(); element.remove();
        if (returnToLogin) window.SmoofEditorTourBridge?.prepareStep?.('downloads');
        const target = previous?.isConnected ? previous : returnToLogin ? document.querySelector('[data-smoof-production-login-note]:not([hidden]) [data-smoof-design-login]') : null;
        target?.focus({ preventScroll: true });
      }
    };
    const protectKeys = event => {
      event.stopImmediatePropagation();
      if (locked || event.key === 'Escape') event.preventDefault();
      if (!locked && event.key === 'Escape') view.close();
    };
    window.addEventListener('keydown', protectKeys, true);
    element.addEventListener('cancel', event => { event.preventDefault(); if (!locked) view.close(); });
    element.setAttribute('aria-busy', 'true');
    (document.body || document.documentElement).appendChild(element);
    element.showModal();
    return view;
  }
  function failure(view, message, retry, download = false) {
    view.release();
    view.copy.textContent = message;
    if (download) {
      const save = document.createElement('button');
      save.dataset.smoofLoginSave = 'true';
      save.textContent = text('Design-Datei herunterladen', 'Download design file');
      save.onclick = async () => {
        view.lock();
        view.element.querySelectorAll('button').forEach(button => { button.disabled = true; });
        try {
          const result = await window.SmoofDesignFileTools?.save();
          if (!result) throw new Error('Save failed');
          view.copy.textContent = text('Die Design-Datei wurde zum Download bereitgestellt. Bewahren Sie sie auf und öffnen Sie sie nach der Anmeldung im Downloadmenü.', 'Your design file is ready to download. Keep it and open it from Downloads after signing in.');
        } catch (_) { view.copy.textContent = text('Die Design-Datei konnte nicht erstellt werden. Bitte versuchen Sie es erneut.', 'The design file could not be created. Please try again.'); }
        finally { view.release(); view.element.querySelectorAll('button').forEach(button => { button.disabled = false; }); save.focus(); }
      };
      view.element.append(save);
    }
    if (retry) {
      const button = document.createElement('button'); button.textContent = text('Erneut versuchen', 'Try again');
      button.onclick = () => { view.close(); retry(); }; view.element.append(button);
    }
    const close = document.createElement('button'); close.textContent = text('Zurück zum Design', 'Back to design');
    close.onclick = () => view.close(); view.element.append(close);
    view.element.querySelector('button')?.focus();
  }
  async function ready() {
    const until = Date.now() + 30000;
    while (Date.now() < until) {
      if (window.SmoofDesignFileTools?.exportPayload && window.SmoofTextTools &&
          window.SmoofCustomPictogramTools?.applyHistoryState && window.SmoofDesignRenderer &&
          window.SmoofDesignModeContract?.inputFor?.('personalized') &&
          (Number(window.SmoofProductProfiles?.current?.surfaceCount) !== 2 || window.SmoofSurfaceStore?.withSurfaces)) return;
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    throw new Error('Editor not ready');
  }
  async function begin(rawUrl, trigger = document.activeElement) {
    if (busy) return false;
    busy = true;
    const view = cover(text('Design wird vor der Anmeldung gesichert …', 'Saving your design before sign-in …'), trigger);
    try {
      const login = new URL(rawUrl, location.origin);
      if (login.origin !== location.origin || !/\/(?:customer_authentication\/login|account\/login)\/?$/.test(login.pathname)) throw new Error('Invalid login destination');
      await ready();
      const side = window.SmoofSurfaceStore?.getActive?.() || 'front';
      const payload = await window.SmoofDesignFileTools.exportPayload();
      if (!payload) throw new Error('Draft capture failed');
      const id = crypto.randomUUID();
      const record = { id, created: Date.now(), pathname: location.pathname, side, payload };
      const older = await stored('readonly', store => store.getAll());
      for (const item of older) {
        if (Date.now() - item.created > MAX_AGE) await stored('readwrite', store => store.delete(item.id));
      }
      await stored('readwrite', store => store.put(record));
      const verified = await stored('readonly', store => store.get(id));
      if (JSON.stringify(verified) !== JSON.stringify(record)) throw new Error('Draft verification failed');
      const target = new URL(location.href);
      for (const key of ['section_id', 'sections', '_smoof_session']) target.searchParams.delete(key);
      target.searchParams.set(PARAM, id);
      login.searchParams.set(login.searchParams.has('return_to') ? 'return_to' : 'return_url', target.pathname + target.search + target.hash);
      location.assign(login.href);
      return true;
    } catch (_) {
      failure(view, text('Ihr Design konnte nicht sicher zwischengespeichert werden. Sie bleiben im Editor. Bitte laden Sie zuerst Ihre Design-Datei herunter und versuchen Sie die Anmeldung erneut.', 'Your design could not be saved safely. You remain in the editor. Download your design file first, then try signing in again.'), () => begin(rawUrl, trigger), true);
      return false;
    } finally { busy = false; }
  }
  async function resume() {
    const id = new URL(location.href).searchParams.get(PARAM);
    if (!id || busy) return false;
    busy = true;
    const view = cover(text('Ihr Design wird wiederhergestellt …', 'Restoring your design …'));
    try {
      const record = await stored('readonly', store => store.get(id));
      if (!record || record.pathname !== location.pathname || Date.now() - record.created > MAX_AGE) throw new Error('Draft missing or expired');
      await ready();
      const mode = window.SmoofDesignModeContract.inputFor('personalized');
      if (!mode.checked) mode.click();
      await new Promise(resolve => setTimeout(resolve, 800));
      const restored = await window.SmoofDesignFileTools.restore(record.payload, { confirmReplace: false });
      if (!restored) throw new Error('Draft restore failed');
      if (record.side === 'back' && window.SmoofSurfaceStore?.switchTo) await window.SmoofSurfaceStore.switchTo('back');
      const target = new URL(location.href); target.searchParams.delete(PARAM);
      history.replaceState(history.state, '', target.href);
      try { await stored('readwrite', store => store.delete(id)); } catch (_) { /* Restored design is already safe; stale backup can expire. */ }
      view.copy.textContent = text('Design wiederhergestellt. Anmeldestatus wird geprüft …', 'Design restored. Checking sign-in status …');
      await window.SmoofProductionAccess?.refresh();
      didRestore = true;
      view.close();
      window.dispatchEvent(new CustomEvent('smoof:login-draft-restored'));
      return true;
    } catch (_) {
      failure(view, text('Das Design konnte noch nicht wiederhergestellt werden. Bitte versuchen Sie es erneut. Eine vorhandene Sicherung bleibt erhalten.', 'Your design could not be restored yet. Please try again. Any saved draft is retained.'), resume);
      return false;
    } finally { busy = false; }
  }
  document.addEventListener('click', event => {
    const path = event.composedPath();
    const account = path.find(node => node?.tagName === 'SHOPIFY-ACCOUNT');
    const session = document.querySelector('[data-smoof-customer-session]');
    if (account && session?.dataset.authenticated === 'false' && !window.SmoofProductionAccess?.allowed() &&
        window.SmoofDesignModeContract?.inputFor?.('personalized')?.checked && session.dataset.loginUrl) {
      event.preventDefault(); event.stopImmediatePropagation();
      begin(session.dataset.loginUrl, account);
      return;
    }
    const link = path.find(node => node?.tagName === 'A' && node.href);
    if (!link) return;
    const url = new URL(link.href, location.origin);
    if (url.origin !== location.origin || !/\/(?:customer_authentication\/login|account\/login)\/?$/.test(url.pathname)) return;
    event.preventDefault(); event.stopImmediatePropagation();
    begin(link.href, link);
  }, true);
  window.SmoofLoginDraft = Object.freeze({ version: '2.0.0', begin, resume, restored: () => didRestore });
  if (new URL(location.href).searchParams.has(PARAM)) resume();
})();
